/**
 * Générateur — Additionner plusieurs nombres décimaux relatifs.
 * Attendu ID : 709 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 4)
 *
 * Plan de cartes :
 *
 *   Carte « groupes » — Ex.6 Sommes de plusieurs termes
 *     texte_trou_saisie avec ÉTAPES (la méthode du cadrage : somme des
 *     positifs, somme des négatifs, résultat) + qcm_unique (résultat seul).
 *     4 à 6 termes mêlés (entiers et décimaux à une décimale, automatisme B1).
 *
 *   Carte « astuce » — Ex.7 Regrouper astucieusement
 *     qcm_unique (« quels sont les deux termes opposés ? » — reformulation
 *     DÉCIDABLE du « regroupement le plus astucieux » : la paire d'opposés
 *     est UNIQUE par construction) + texte_trou_saisie (« le terme opposé à
 *     x est {{-x}} ; la somme totale vaut {{r}} » — l'ordre des trous est
 *     fixé par la phrase).
 *
 *   Carte « pyramide » — Ex.4 Pyramides de nombres
 *     texte_trou_saisie : base de 4 nombres donnée, les 6 cases supérieures à
 *     calculer (enchaînement auto-contrôlé, décimaux à une décimale, B1).
 *
 *   Carte « note » — Ex.9 La note du QCM
 *     texte (saisie : note d'une copie de 8 réponses), texte_trou_saisie
 *     (note la plus basse / la plus haute), qcm_unique (la combinaison qui
 *     donne une note cible — CIBLE CHOISIE PAR ÉNUMÉRATION pour garantir la
 *     solution unique, la mise en garde du doc devient un invariant).
 *
 * Politique : résultats signés imposés en consigne, tiret clavier,
 * arithmétique en dixièmes entiers.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 709;

const L_GROUPES = 'Regrouper les positifs et les négatifs';
const L_ASTUCE = 'Regrouper astucieusement';
const L_PYRAMIDE = 'Pyramides de nombres';
const L_NOTE = 'La note du QCM';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frD = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signeD = (d) => (d === 0 ? '0' : d < 0 ? frD(d) : `+${frD(d)}`);
// écriture mêlée : positifs nus, négatifs entre parenthèses
const ecr = (v, premier = false) => (v < 0 && !premier ? `(${signeD(v)})` : frD(v));

const CONSIGNE_RES = 'Écris chaque réponse avec son signe (ex. : -5 ou +8), ou 0 si elle est nulle.';

const exprDe = (termes) => termes.map((v, i) => ecr(v, i === 0)).join(' + ');

// ═════════ Carte 1 — Regrouper positifs et négatifs (Ex.6) ═════════

/** 4-6 termes : ≥ 2 positifs, ≥ 2 négatifs, décimaux ou entiers (dixièmes). */
const tirerTermes = () => {
    const n = randomChoice([4, 5, 6]);
    const nPos = randInt(2, n - 2);
    const val = () => (randomChoice([true, false]) ? randInt(2, 29) * 10 : randInt(11, 99));
    const termes = [];
    for (let i = 0; i < n; i++) termes.push((i < nPos ? 1 : -1) * val());
    return shuffle(termes);
};

const genGroupesSaisie = () => {
    const termes = tirerTermes();
    const lettre = randomChoice(['A', 'B', 'C', 'D']);
    const P = termes.filter(v => v > 0).reduce((s, v) => s + v, 0);
    const N = termes.filter(v => v < 0).reduce((s, v) => s + v, 0);
    const phrase = `${lettre} = ${exprDe(termes)} — somme des termes positifs : {{${signeD(P)}}} ; `
        + `somme des termes négatifs : {{${signeD(N)}}} ; résultat : {{${signeD(P + N)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Calcule ${lettre} en regroupant les termes positifs ensemble et les termes négatifs ensemble.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_GROUPES), mode: 'saisie' });
    payload._meta.params = { termes };
    return payload;
};

const genGroupesQcm = () => {
    const termes = tirerTermes();
    const lettre = randomChoice(['A', 'B', 'C', 'D']);
    const P = termes.filter(v => v > 0).reduce((s, v) => s + v, 0);
    const N = termes.filter(v => v < 0).reduce((s, v) => s + v, 0);
    const total = P + N;
    const correct = signeD(total);
    const cands = [signeD(-total), signeD(P - N), signeD(total + 10), signeD(total - 10)];
    const distracteurs = [];
    cands.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Que vaut ${lettre} = ${exprDe(termes)} ?</b></p>`,
        opts(L_GROUPES));
    payload._meta.params = { termes };
    return payload;
};

// ═════════ Carte 2 — Regrouper astucieusement (Ex.7) ═════════

/** 5 termes dont UNE SEULE paire d'opposés ; aucun autre couple de même valeur absolue. */
const tirerAstuce = () => {
    const x = randomChoice([true, false]) ? randInt(3, 89) * 10 : randInt(101, 899);
    const absolues = new Set([Math.abs(x)]);
    const autres = [];
    for (let essai = 0; essai < 60 && autres.length < 3; essai++) {
        const v = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 95) * 10;
        if (!absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    }
    [130, -270, 460].forEach(v => {
        if (autres.length < 3 && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    });
    return { x, termes: shuffle([x, -x, ...autres]), reste: autres.reduce((s, v) => s + v, 0) };
};

const genAstuceQcm = () => {
    const { x, termes } = tirerAstuce();
    // la paire est citée dans l'ORDRE D'APPARITION de la somme (contrat solveur)
    const [p1, p2] = termes.filter(v => Math.abs(v) === Math.abs(x));
    const correct = `${frD(p1)} et ${frD(p2)}`;
    const faux = [];
    const paires = shuffle(termes.flatMap((a, i) => termes.map((b, j) => [i, j]).filter(([i2, j2]) => i2 < j2)));
    for (const [i, j] of paires) {
        if (faux.length >= 3) break;
        if (termes[i] + termes[j] !== 0) {
            const p = `${frD(termes[i])} et ${frD(termes[j])}`;
            if (!faux.includes(p)) faux.push(p);
        }
    }
    const payload = makeQcm([correct, ...faux], correct,
        `<p>On veut calculer <strong>${exprDe(termes)}</strong> le plus simplement possible. `
        + '<b>Quels sont les deux termes opposés (leur somme vaut 0) qu\'on a intérêt à regrouper d\'abord ?</b></p>',
        opts(L_ASTUCE));
    payload._meta.params = { x, termes };
    return payload;
};

const genAstuceSaisie = () => {
    const { x, termes, reste } = tirerAstuce();
    const phrase = `Dans ${exprDe(termes)}, le terme opposé à ${ecr(x, true)} est {{${signeD(-x)}}} ; `
        + `en regroupant ces deux termes (somme 0), la somme totale vaut {{${signeD(reste)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Repère les opposés et calcule astucieusement.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_ASTUCE), mode: 'saisie' });
    payload._meta.params = { x, termes, reste };
    return payload;
};

// ═════════ Carte 3 — Pyramides (Ex.4) ═════════

const genPyramideSaisie = () => {
    const base = [];
    const absolues = new Set();
    for (let essai = 0; essai < 60 && base.length < 4; essai++) {
        const v = (randomChoice([true, false]) ? 1 : -1) * (randInt(11, 95));
        if (!absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); base.push(v); }
    }
    [-12, 80, -49, -83].forEach(v => { if (base.length < 4) base.push(v); });
    const e2 = [base[0] + base[1], base[1] + base[2], base[2] + base[3]];
    const e3 = [e2[0] + e2[1], e2[1] + e2[2]];
    const sommet = e3[0] + e3[1];
    const phrase = `Étage du milieu : {{${signeD(e2[0])}}} ; {{${signeD(e2[1])}}} ; {{${signeD(e2[2])}}}. `
        + `Étage suivant : {{${signeD(e3[0])}}} ; {{${signeD(e3[1])}}}. Sommet : {{${signeD(sommet)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p>Dans une pyramide de nombres, chaque case est la somme des deux cases juste en dessous. '
        + `<b>Base, de gauche à droite : ${base.map(frD).join(' | ')}.</b> Complète les étages, de bas en haut. ${CONSIGNE_RES}</p>`,
        { ...opts(L_PYRAMIDE), mode: 'saisie' });
    payload._meta.params = { base };
    return payload;
};

// ═════════ Carte 4 — La note du QCM (Ex.9) ═════════

const tirerBareme = () => {
    const S = randomChoice([-1, -2]);            // sans réponse
    const F = -randInt(Math.abs(S) + 1, 4);      // fausse : toujours plus pénalisante
    return { B: randInt(3, 7), F, S };           // 25 barèmes possibles
};

const texteBareme = (bar) =>
    `Un professeur note un QCM de 8 questions ainsi : réponse fausse (F) : ${bar.F} points ; `
    + `sans réponse (S) : ${bar.S} point${bar.S < -1 ? 's' : ''} ; réponse bonne (B) : +${bar.B} points.`;

const genNoteCalcul = () => {
    const bar = tirerBareme();
    const nb = randInt(2, 4);
    const nf = randInt(1, 3);
    const ns = 8 - nb - nf;
    const copie = shuffle([...Array(nb).fill('B'), ...Array(nf).fill('F'), ...Array(ns).fill('S')]);
    const note = nb * bar.B + nf * bar.F + ns * bar.S;
    const payload = makeSaisie(signeD(note * 10),
        `<p>${texteBareme(bar)} <b>Calcule la note d'une copie dont les résultats sont : ${copie.join(' ; ')}.</b> `
        + 'Écris la note avec son signe (ex. : -5 ou +8), ou 0 si elle est nulle.</p>',
        opts(L_NOTE));
    payload._meta.params = { bar, copie, note };
    return payload;
};

const genNoteExtremes = () => {
    const bar = tirerBareme();
    const phrase = `Note la plus basse possible : {{${signeD(8 * bar.F * 10)}}} ; note la plus haute possible : {{${signeD(8 * bar.B * 10)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p>${texteBareme(bar)} <b>Quelles sont les notes extrêmes qu'un élève peut obtenir ?</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_NOTE), mode: 'saisie' });
    payload._meta.params = { bar };
    return payload;
};

const comboTxt = (b, f, s) =>
    `${b} bonne${b > 1 ? 's' : ''}, ${f} fausse${f > 1 ? 's' : ''}, ${s} sans réponse`;

/** Toutes les répartitions (b, f, s) de 8 questions avec leur note. */
const toutesCombos = (bar) => {
    const combos = [];
    for (let b = 0; b <= 8; b++) {
        for (let f = 0; f <= 8 - b; f++) {
            const s = 8 - b - f;
            combos.push({ b, f, s, note: b * bar.B + f * bar.F + s * bar.S });
        }
    }
    return combos;
};

const genNoteCombinaison = () => {
    const bar = tirerBareme();
    const combos = toutesCombos(bar);
    const parNote = new Map();
    combos.forEach(c => {
        if (!parNote.has(c.note)) parNote.set(c.note, []);
        parNote.get(c.note).push(c);
    });
    // cible = une note atteignable d'UNE SEULE façon (hors extrêmes triviaux)
    const uniques = [...parNote.entries()]
        .filter(([note, cs]) => cs.length === 1 && note !== 8 * bar.B && note !== 8 * bar.F);
    const [note, [bonne]] = uniques.length > 0
        ? randomChoice(uniques)
        : [8 * bar.B, [{ b: 8, f: 0, s: 0, note: 8 * bar.B }]]; // repli (toujours unique)
    const correct = comboTxt(bonne.b, bonne.f, bonne.s);
    const faux = [];
    for (const c of shuffle(combos)) {
        if (faux.length >= 3) break;
        if (c.note !== note) {
            const t = comboTxt(c.b, c.f, c.s);
            if (!faux.includes(t)) faux.push(t);
        }
    }
    const payload = makeQcm([correct, ...faux], correct,
        `<p>${texteBareme(bar)} <b>Un élève a obtenu la note ${signeD(note * 10)}. Quels sont ses résultats ?</b></p>`,
        opts(L_NOTE));
    payload._meta.params = { bar, note, bonne };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    groupes_texte_trou_saisie: genGroupesSaisie,
    groupes_qcm_unique: genGroupesQcm,
    astuce_opposes_qcm_unique: genAstuceQcm,
    astuce_texte_trou_saisie: genAstuceSaisie,
    pyramide_texte_trou_saisie: genPyramideSaisie,
    note_calcul_texte: genNoteCalcul,
    note_extremes_texte_trou_saisie: genNoteExtremes,
    note_combinaison_qcm_unique: genNoteCombinaison,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM',
    opposes_qcm_unique: 'QCM Opposés', calcul_texte: 'Saisie',
    extremes_texte_trou_saisie: 'Extrêmes (saisie)', combinaison_qcm_unique: 'QCM Combinaison',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'groupes', label: L_GROUPES, formatsList: buildFormats('groupes') },
    { id: 'astuce', label: L_ASTUCE, formatsList: buildFormats('astuce') },
    { id: 'pyramide', label: L_PYRAMIDE, formatsList: buildFormats('pyramide') },
    { id: 'note', label: L_NOTE, formatsList: buildFormats('note') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
